import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatUSD, formatUSDCompact, monthName } from "@/lib/format";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Briefcase, Coins, Receipt as ReceiptIcon } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import CoreIntegrationPanel from "@/components/CoreIntegrationPanel";

const KPI_RAIL = ({ totals }) => (
  <div className="kpi-rail grid grid-cols-2 md:grid-cols-4">
    {[
      ["Closed Deals", totals.transaction_count, false, "kpi-deals"],
      ["Sales Volume", formatUSDCompact(totals.total_sales), false, "kpi-sales"],
      ["Gross Comm.", formatUSDCompact(totals.gross_commission), false, "kpi-gross"],
      ["Net Comm.", formatUSDCompact(totals.net_commission), false, "kpi-net"],
      ["FMLS 0.12%", formatUSDCompact(totals.fmls_fees), false, "kpi-fmls"],
      ["Broker Fees", formatUSDCompact(totals.broker_fees), false, "kpi-broker"],
      ["Ref + Txn Fees", formatUSDCompact(totals.other_fees), false, "kpi-other-fees"],
      ["Net Profit", formatUSDCompact(totals.net_profit), totals.net_profit >= 0 ? "profit" : "expense", "kpi-profit"],
    ].map(([label, value, color, testid], i) => (
      <div
        key={label}
        className={`px-6 py-5 border-r border-neutral-200 last:border-r-0 ${i % 4 === 3 ? "md:border-r-0" : ""} ${i >= 4 ? "border-t" : ""} ${i >= 2 && i < 4 ? "border-t md:border-t-0" : ""}`}
        data-testid={testid}
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">{label}</div>
        <div
          className="font-mono text-2xl tracking-tight"
          style={{ color: color === "profit" ? "var(--profit)" : color === "expense" ? "var(--expense)" : "var(--ink-900)" }}
        >
          {value}
        </div>
      </div>
    ))}
  </div>
);

export default function DashboardPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [r1, r2] = await Promise.all([
        api.get("/report/summary", { params: { year } }),
        api.get("/deals"),
      ]);
      if (cancelled) return;
      setData(r1.data);
      setRecentDeals(r2.data.slice(0, 6));
    };
    load();
    // Auto-refresh totals when the tab regains focus — catches deals moved to Closed elsewhere.
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [year]);

  const monthlyChart = useMemo(() => {
    if (!data) return [];
    return data.monthly.map((m) => ({ ...m, label: monthName(m.month) }));
  }, [data]);

  const stageCounts = useMemo(() => {
    const counts = { prospecting: 0, active: 0, pending: 0, closed: 0, lost: 0 };
    recentDeals.forEach((d) => { counts[d.stage] = (counts[d.stage] || 0) + 1; });
    return counts;
  }, [recentDeals]);

  if (!data) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

  const { totals } = data;
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 4; y--) yearOptions.push(y);

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="dashboard-page">
      {/* Title row */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Control Room</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Dashboard</h1>
          <p className="text-sm text-neutral-500 mt-2">Your year-to-date performance, deductions, and net profit.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Tax Year</span>
          <div className="flex">
            {yearOptions.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-4 py-2 text-sm font-mono border border-r-0 last:border-r border-neutral-300 transition-colors ${
                  y === year ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
                data-testid={`year-tab-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI rail */}
      <KPI_RAIL totals={totals} />

      {/* CORE Realty widget */}
      <div className="mt-6">
        <CoreIntegrationPanel compact />
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t-0 border border-neutral-200 mt-0">
        {/* Monthly chart */}
        <div className="lg:col-span-2 border-r-0 lg:border-r border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Monthly Cashflow</div>
              <div className="font-serif text-2xl">Commission vs. Expenses</div>
            </div>
            <TrendingUp className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
          </div>
          <div className="h-72" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyChart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatUSDCompact(v)} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", color: "#FFF", border: "none", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
                  formatter={(v, n) => [formatUSD(v), n === "net" ? "Net Comm." : n === "gross" ? "Gross Comm." : "Expenses"]}
                  cursor={{ fill: "#FAFAFA" }}
                />
                <Bar dataKey="gross" fill="#0A0A0A" />
                <Bar dataKey="net" fill="#525252" />
                <Bar dataKey="expenses" fill="#7F1D1D" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline snapshot */}
        <div className="p-6 border-t lg:border-t-0 border-neutral-200">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Pipeline</div>
          <div className="font-serif text-2xl mb-4">Stage Snapshot</div>
          <div className="space-y-3" data-testid="stage-snapshot">
            {[
              ["prospecting", "Prospecting"],
              ["active", "Active"],
              ["pending", "Pending"],
              ["closed", "Closed"],
              ["lost", "Lost"],
            ].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between border-b border-neutral-100 pb-2">
                <span className="text-sm text-neutral-700">{label}</span>
                <span className="font-mono text-lg">{stageCounts[k] || 0}</span>
              </div>
            ))}
          </div>
          <Link
            to="/pipeline"
            className="mt-6 inline-flex items-center gap-1 text-xs font-sans uppercase tracking-[0.2em] text-neutral-900 hover:underline underline-offset-4"
            data-testid="dashboard-open-pipeline"
          >
            Open pipeline <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </div>

      {/* Highlights row */}
      <div className="grid grid-cols-1 md:grid-cols-3 border border-t-0 border-neutral-200">
        <Link to="/pipeline" className="p-6 border-r border-neutral-200 hover:bg-neutral-50 transition-colors" data-testid="link-pipeline-card">
          <Briefcase className="h-5 w-5 mb-3" strokeWidth={1.5} />
          <div className="font-serif text-xl mb-1">Deal Pipeline</div>
          <p className="text-sm text-neutral-500">Drag deals across stages. Auto-rolls closed deals into your tax report.</p>
        </Link>
        <Link to="/expenses" className="p-6 border-r border-neutral-200 hover:bg-neutral-50 transition-colors" data-testid="link-expenses-card">
          <ReceiptIcon className="h-5 w-5 mb-3" strokeWidth={1.5} />
          <div className="font-serif text-xl mb-1">Expenses</div>
          <p className="text-sm text-neutral-500">Log deductibles like mileage, MLS dues, marketing, and E&amp;O.</p>
        </Link>
        <Link to="/report" className="p-6 hover:bg-neutral-50 transition-colors group" data-testid="link-report-card">
          <Coins className="h-5 w-5 mb-3" strokeWidth={1.5} />
          <div className="flex items-center gap-2">
            <div className="font-serif text-xl mb-1">Year-End Tax Report</div>
            <ArrowUpRight className="h-4 w-4 text-neutral-400 group-hover:text-neutral-900" strokeWidth={1.5} />
          </div>
          <p className="text-sm text-neutral-500">Excel, PDF, CSV exports. Print-ready P&amp;L summary.</p>
        </Link>
      </div>
    </div>
  );
}
